import React, { useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as Actions from "../actions/Actions.js";
import { HashLink as Link } from "react-router-hash-link";
import Helper from "../functions/Helper.js";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import LikeButton from "../components/LikeButton.js";
import StyleModal from "../components/StyleModal.js";
import Modal from "react-bootstrap/Modal";
import produce from "immer";
import Form from "react-bootstrap/Form";
import { Helmet } from "react-helmet";
import { useShoe } from "../hooks/useShoe.js";

const getJsonLD = (shoe) => {
  return [
    {
      "@context": "http://schema.org",
      "@type": "ItemList",
      numberOfItems: shoe.styles.length,
      itemListElement: shoe.styles.map((style, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "thing",
          name: shoe.shoe_name + " - " + style.name,
          //sku:style.sku,
          image: Helper.add_options_img(style.picture_url, 1170),
          url:
            "https://www.thehoopsgeek.com/shoe-reviews/" +
            shoe.url.toLowerCase() +
            "-colorways/#" +
            style.sku,
        },
      })),
    },
    {
      "@context": "http://schema.org",
      "@type": "Product",
      name: shoe.shoe_name,
      image:
        shoe.hasOwnProperty("pictures") &&
        "https://www.thehoopsgeek.com/" + shoe.pictures[1].url,
      description: shoe.short_summary,
      brand: shoe.brand,
      ...(shoe.rating !== "NA"
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              bestRating: "10",
              worstRating: "1",
              ratingValue: shoe.rating,
              ratingCount: shoe.review_amount,
            },
          }
        : {}),
      ...Helper.getOffers(shoe, shoe.colorways),
    },
  ];
};

const sortStyles = (styles, sortBy) => {
  let returnStyles = produce(styles, (draftStyles) => {
    if (sortBy == 0) {
      draftStyles.sort((a, b) => Helper.sortStylesByPrice(a, b));
    } else if (sortBy == 1) {
      draftStyles.sort((a, b) => Helper.sortStylesByLikes(a, b));
    }
  });
  return returnStyles;
};

const StoreOutput = ({ style }) => {
  if (style.colorways?.length > 0) {
    var firstColorway = style.colorways[0];
    var discount = Math.round(
      (1 - firstColorway.price_discount / firstColorway.list_price) * 100
    );
    return (
      <React.Fragment>
        <div
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            marginTop: 5,
            color: "var(--grey-800)",
          }}
        >
          {style.colorways.length > 1
            ? "in " +
              (style.colorways?.length + style.resells?.length) +
              " shops"
            : "from " + firstColorway.shop_name}
        </div>
        <div
          style={{
            marginTop: 2,
            lineHeight: 1.3,
            display: "flex",
            flexDirection: "row",
          }}
        >
          <div
            style={{
              color: discount > 1 ? "#e32b2b" : "black",
              fontWeight: discount > 1 ? 500 : 400,
            }}
          >
            ${firstColorway.price_discount}
          </div>
          {discount > 1 && (
            <div
              style={{
                color: "#767676",
                textDecorationLine: "line-through",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textDecorationStyle: "solid",
                marginLeft: 5,
                fontSize: 14,
              }}
            >
              ${Math.round(Number(firstColorway.list_price))}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  } else if (style.resells?.length > 0) {
    var firstResell = style.resells[0];
    return (
      <React.Fragment>
        <div
          style={{
            fontSize: 14,
            lineHeight: 1.3,
            marginTop: 3,
            color: "#767676",
          }}
        >
          {style.resells.length > 1
            ? "from " + style.resells.length + " Resellers"
            : "from " + firstResell.shop_name}
        </div>
        <div
          style={{
            marginTop: 2,
            lineHeight: 1.3,
            display: "flex",
            flexDirection: "row",
          }}
        >
          <div style={{ color: "black", fontWeight: 400 }}>
            ${firstResell.price_discount}
          </div>
        </div>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <div style={{ marginTop: 2 }}>
          <div style={{ fontSize: 14, color: "#767676" }}>No stores found</div>
        </div>
      </React.Fragment>
    );
  }
};

function ColorwayPage(props) {
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [activeStyleId, setActiveStyleId] = useState(0);
  const [sortingStyles, setSortingStyles] = useState(false);

  let memoShoe = useShoe(
    props.url.toLowerCase(),
    props.currentShoe,
    props.data,
    props.actions.shoeData
  );

  let sortedStyles =
    memoShoe &&
    memoShoe.hasOwnProperty("styles") &&
    sortStyles(memoShoe.styles, props.sortStylesBy);

  let activeStyle = sortedStyles
    ? {
        ...sortedStyles.filter((style) => style.style_id === activeStyleId)[0],
        shoe_name: memoShoe.shoe_name,
        shoe_url: memoShoe.url,
      }
    : false;

  let cheapestPrice = memoShoe?.discount;
  let offersExist = memoShoe?.colorways
    ? memoShoe.colorways.length !== 0
    : false;

  return (
    <div>
      {memoShoe && memoShoe.hasOwnProperty("colorways") ? (
        <>
          <Helmet>
            <title>
              {memoShoe.shoe_name +
                " Colorways - " +
                memoShoe.styles.length +
                " Styles " +
                (offersExist
                  ? "Starting from $" + cheapestPrice.toFixed(2)
                  : "")}
            </title>
            <meta property="og:image:width" content="1170" />
            <meta property="og:image:height" content="658" />
            <meta
              property="og:image"
              content={
                "https://www.thehoopsgeek.com" + memoShoe.pictures[1].url
              }
            />
            <meta
              name="description"
              content={
                "See high-res pictures of " +
                memoShoe.styles.length +
                " " +
                memoShoe.shoe_name +
                " colorways and compare prices among the most popular online shops"
              }
            />
            <script type="application/ld+json">
              {JSON.stringify(getJsonLD(memoShoe))}
            </script>
          </Helmet>

          <Modal
            dialogClassName="thg_style_modal"
            onHide={() => {
              setShowStyleModal(false);
            }}
            show={showStyleModal}
          >
            <StyleModal
              linkId={"colorways"}
              hideModal={() => {
                setShowStyleModal(false);
              }}
              activeStyle={activeStyle}
            />
          </Modal>
          <div>
            <p
              style={{ marginBottom: 10, marginTop: 10 }}
              id="breadcrumbs_react"
            >
              <span>
                <Link to={"/shoe-reviews/"}>Basketball Shoes</Link>{" "}
                &nbsp;/&nbsp;
                <span>
                  <Link
                    to={"/shoe-reviews/" + memoShoe.url.toLowerCase() + "/"}
                  >
                    {memoShoe.shoe_name}
                  </Link>
                </span>
              </span>
            </p>
          </div>

          <div className="d-md-flex" style={{ marginBottom: 10 }}>
            <div>
              <div style={{ marginTop: 0 }}>
                <h1 className="thg_h1_size">
                  <span>{memoShoe.shoe_name + " "}</span>
                  Colorways
                </h1>
              </div>
            </div>
            <div
              className="d-flex mt-md-2 justify-content-md-end"
              style={{ flex: 1 }}
            >
              <Form.Control
                css={{
                  "@media (min-width:768px)": { marginLeft: 20, marginTop: 0 },
                  fontSize: 16,
                  marginTop: 15,
                  width: "auto",
                }}
                value={props.sortStylesBy}
                onChange={(e) => {
                  setSortingStyles(true);
                  setTimeout(() => {
                    setSortingStyles(false);
                  }, 500);
                  props.actions.setSortStylesBy(e.target.value);
                }}
                as="select"
              >
                <option value={0}>sorted by price</option>
                <option value={1}>sorted by likes</option>
              </Form.Control>
            </div>
          </div>

          <Row
            style={{
              opacity: sortingStyles ? 0.5 : 1,
              margin: "10px -7px 100px -7px",
            }}
          >
            {sortedStyles.map((style) => (
              <Col
                id={style.sku}
                lg={3}
                md={4}
                sm={6}
                xs={6}
                key={style.style_id}
                style={{ marginBottom: 50, padding: 7 }}
              >
                <div
                  style={{
                    position: "relative",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    width: "100%",
                  }}
                  onClick={() => {
                    setShowStyleModal(true);
                    setActiveStyleId(style.style_id);
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#f8f8f8",
                      paddingTop: "90%",
                      position: "relative",
                    }}
                  >
                    <picture>
                      <source
                        type="image/webp"
                        srcSet={Helper.create_source_set(
                          { master: style.picture_url, thumbnails: "1" },
                          undefined,
                          "webp"
                        )}
                        sizes={"286px"}
                      />
                      <source
                        type="image/png"
                        srcSet={Helper.create_source_set({
                          master: style.picture_url,
                          thumbnails: "1",
                        })}
                        sizes={"286px"}
                      />
                      <img
                        title={"SKU: " + style.sku}
                        style={{
                          position: "absolute",
                          bottom: 0,
                          padding: "6% 6%",
                          transform: style.mirror == "1" ? "scaleX(-1)" : "",
                        }}
                        alt={style.name}
                        width={style.width}
                        height={style.height}
                        sizes={"286px"}
                        srcSet={Helper.create_source_set({
                          master: style.picture_url,
                          thumbnails: "1",
                        })}
                      />
                    </picture>
                  </div>

                  <div
                    className="pl-sm-2"
                    style={{ marginTop: 10, paddingRight: 5 }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        zIndex: 1,
                        padding: "10px 5px",
                      }}
                    >
                      <LikeButton
                        containerStyle={{
                          padding: "0px 0px 5px",
                          textAlign: "center",
                        }}
                        showCounter
                        likeCounter={style.likes}
                        style_id={style.style_id}
                        shoe_id={style.shoe_id}
                        style={{ fontSize: 26 }}
                      />
                    </div>
                    <h2 style={{ margin: 0, lineHeight: 1.6 }}>
                      <div style={{ fontSize: 14, color: "var(--grey-800)" }}>
                        SKU:{" " + style.sku + " "}
                      </div>
                      <div
                        style={{
                          fontSize: 16,
                          lineHeight: 1.4,
                          fontWeight: 600,
                          wordBreak: "break-word",
                          color: "black",
                        }}
                      >
                        {style.name}
                      </div>
                    </h2>
                    <StoreOutput style={style} />
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {memoShoe.player !== "No Signature" && (
            <div style={{ fontWeight: 600 }}>
              See more{" "}
              <Link
                to={
                  "/shoe-reviews/" +
                  memoShoe.player
                    .toLowerCase()
                    .replace(" ", "-")
                    .replace("’", "") +
                  "-basketball-shoes/"
                }
              >
                {memoShoe.player} Shoes
              </Link>
            </div>
          )}
        </>
      ) : (
        <div>Loading Styes...</div>
      )}
    </div>
  );
}

export default connect(
  (state) => ({ ...state.data, ...state.persistData }),
  (dispatch) => ({
    actions: bindActionCreators(Actions, dispatch),
  })
)(ColorwayPage);
